import { useLayoutEffect, useRef, useState } from "react";

/**
 * The pixel cap applied to a table's name/email identity column.
 *
 * NOTE: extracted from pages/employee/EmployeeList.tsx, which is where this
 * technique was established and tuned. The employee list still carries its own
 * copy; this module exists so the feedback list could adopt the SAME number
 * instead of inventing a second one, and so a future change moves both tables
 * together. EmployeeList should migrate onto this hook.
 *
 * WHY MEASURE INSTEAD OF HARDCODING A WIDTH: the cap has to hold across
 * themes, zoom levels and whatever font the browser actually resolves, none of
 * which a literal `max-w-[140px]` survives. Measuring a representative string
 * in the live document and scaling it keeps the column at roughly "a typical
 * name and a bit of headroom" wherever it renders.
 *
 * WHY THE CLASSES ALONE ARE NOT ENOUGH: `TruncatedText` carries
 * `w-[200px] sm:w-[250px] lg:w-[350px]`, but on a wide screen 350px is more
 * than almost any name or address needs, so nothing is ever clipped. This cap
 * sits on the PARENT and clamps those widths down — it is what actually makes
 * the ellipsis appear.
 */

/**
 * Headroom over the reference string. 1.7 is the value EmployeeList arrived at:
 * enough that an ordinary name shows in full, tight enough that a long one is
 * clipped rather than widening the column.
 */
export const NAME_TRUNCATION_WIDTH_MULTIPLIER = 1.7;

/** The string whose rendered width sets the cap. */
export const TRUNCATION_REFERENCE_TEXT = "admin liri";

/**
 * Font of the reference span. Deliberately `text-base` while the cell renders
 * at `text-sm` — the difference is part of the headroom the multiplier tunes,
 * so changing one without re-tuning the other will move every column.
 */
export const TRUNCATION_REFERENCE_CLS = "text-base font-semibold";

/**
 * Measures `TRUNCATION_REFERENCE_TEXT` once and returns the cap in pixels.
 *
 * Attach `referenceRef` to a hidden span rendering that text at
 * `TRUNCATION_REFERENCE_CLS`, and apply `maxWidth` to the identity cell's text
 * block. `maxWidth` is null on the first paint, before the measurement exists —
 * callers fall back to a literal, as EmployeeList does with '200px'.
 *
 * NOTE: useLayoutEffect, not useEffect, so the width is read before the browser
 * paints and the column never visibly jumps from uncapped to capped.
 */
export const useNameColumnWidth = (
  multiplier: number = NAME_TRUNCATION_WIDTH_MULTIPLIER,
) => {
  const referenceRef = useRef<HTMLSpanElement | null>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    // Measured once. The reference text never changes, so re-measuring on
    // every render would only risk a feedback loop through setState.
    if (referenceRef.current && maxWidth === null) {
      setMaxWidth(
        Math.ceil(referenceRef.current.getBoundingClientRect().width * multiplier),
      );
    }
  }, [maxWidth, multiplier]);

  return { referenceRef, maxWidth };
};
